import { Segmented, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import SocialCardRenderer from "./components/SocialCardRenderer";
import type { InstagramViewType, PreviewType } from "./types";
import { usePreviewSelectorOptions } from "./usePreviewSelectorOptions";

import { integrationsGetter } from "@/store/user/userSlice";
import { useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import type { IntegrationType } from "store/user/userConstants";

export type PostPreviewProps = {
  caption: string;
  mediaUrls: string[];
  publishTo: IntegrationType[] | null;
  existingPost?: Post;
  title?: string;
  link?: string;
  isTemplateVideo: boolean;
};

const PostPreview = ({
  caption,
  mediaUrls,
  existingPost,
  title,
  link,
  publishTo,
  isTemplateVideo,
}: PostPreviewProps) => {
  const integrations = useAppSelector(integrationsGetter);

  const [selectedPreview, setSelectedPreview] =
    useState<PreviewType>("Generic");
  // State for Instagram Post/Story sub-selection
  const [selectedInstagramView, setSelectedInstagramView] =
    useState<InstagramViewType>("post");

  const publishToFilled = useMemo(() => {
    return publishTo || integrations.map((int) => int.type);
  }, [publishTo, integrations]);

  // Use the custom hook primarily for showInstagramSubSelector now
  const { options: segmentedOptions, showInstagramSubSelector } =
    usePreviewSelectorOptions({
      publishTo: publishToFilled,
    });

  // Effect to reset instagram view when main selection changes away from instagram
  useEffect(() => {
    if (selectedPreview !== "instagram") {
      setSelectedInstagramView("post");
    }
  }, [selectedPreview]);

  // Effect to reset main preview if selected option is no longer available
  useEffect(() => {
    const availableValues = new Set(
      segmentedOptions.map((opt: { value: PreviewType }) => opt.value)
    );
    if (!availableValues.has(selectedPreview)) {
      // Reset to Generic or the first available option if Generic isn't one
      const firstAvailable = segmentedOptions.find(
        (opt: { value: PreviewType }) => opt.value !== "Generic"
      )?.value;
      setSelectedPreview(firstAvailable ?? "Generic");
    }
    // Only re-run when the options themselves change
  }, [segmentedOptions, selectedPreview]); // Add selectedPreview dependency

  // Removed renderSocialCard function (moved to SocialCardRenderer.tsx)

  return (
    <div
      className={`flex flex-col justify-start rounded-md h-full w-full border border-solid border-antd-colorBorder p-3 ${
        selectedPreview === "twitter" || selectedPreview === "tiktok"
          ? "bg-antd-colorBgContainer"
          : "bg-antd-colorBgLayout"
      }`}
    >
      <div className="mx-auto flex flex-col items-center flex-shrink-0 mb-2 space-y-2">
        {/* Main Platform Selector */}
        <Segmented<PreviewType>
          value={selectedPreview}
          onChange={(value) => setSelectedPreview(value as PreviewType)}
          options={segmentedOptions} // Use the dynamic options
          disabled={segmentedOptions.length <= 1} // Re-enable the disabled prop
          className=""
        />
        {/* Conditional Instagram Sub-Selector */}
        {showInstagramSubSelector && selectedPreview === "instagram" && (
          <Segmented<InstagramViewType>
            value={selectedInstagramView}
            onChange={(value) =>
              setSelectedInstagramView(value as InstagramViewType)
            }
            options={[
              { label: "Post", value: "post" },
              { label: "Story", value: "story" },
            ]}
            className="" // Make it full width like the main selector
            size="small" // Optional: make it slightly smaller
          />
        )}
      </div>
      {/* Card container - grows and scrolls */}
      <div className="px-4 max-w-md mx-auto flex-grow overflow-y-auto w-full">
        {/* Wrapper div for scaling */}
        <div className="transform scale-90 origin-top w-full">
          {/* Use the new SocialCardRenderer component */}
          <SocialCardRenderer
            selectedPreview={selectedPreview}
            selectedInstagramView={selectedInstagramView}
            showInstagramSubSelector={showInstagramSubSelector}
            integrations={integrations}
            caption={caption}
            mediaUrls={mediaUrls}
            existingPost={existingPost}
            title={title}
            link={link}
            isTemplateVideo={isTemplateVideo}
          />
        </div>
      </div>
      <div className="max-w-md mx-auto w-full text-center flex-shrink-0 mt-1">
        <Typography.Text type="secondary" style={{ fontSize: "0.75rem" }}>
          *Preview may differ slightly from the final post.
        </Typography.Text>
      </div>
    </div>
  );
};

export default PostPreview;
